import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { SharedHeader } from "@/components/SharedHeader";
import RotatingNewsBanner from "@/components/RotatingNewsBanner";

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Terms of Use | AI Customer Support Platform | Call24x7.AI</title>
        <meta name="description" content="Call24x7.AI Terms of Use. Read the terms and conditions for using our AI customer support platform, AI answering service, and AI receptionist service." />
        <link rel="canonical" href="https://call24x7.ai/terms-of-use" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7.AI" />
        
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://call24x7.ai"
          }, {
            "@type": "ListItem",
            "position": 2,
            "name": "Terms of Use",
            "item": "https://call24x7.ai/terms-of-use"
          }]
        })}
        </script>
      </Helmet>
    <div className="min-h-screen bg-background">
      <RotatingNewsBanner />
      <SharedHeader />
      <div className="container mx-auto px-2 sm:px-4 py-8 max-w-4xl pt-32">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <h1 className="text-4xl font-bold mb-2">Terms of Use</h1>
        <p className="text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="space-y-8 text-foreground/90">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing and using Call24x7.AI services, you accept and agree to be bound by the terms and 
              provisions of this agreement. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="leading-relaxed">
              Call24x7.AI provides AI-powered customer support solutions, including but not limited to AI phone agents, 
              AI receptionists, and automated customer service systems. We reserve the right to modify, suspend, or 
              discontinue any part of our services at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
            <p className="leading-relaxed mb-3">When creating an account with us, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
            <p className="leading-relaxed mb-3">You agree not to use our services to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Transmit harmful, threatening, or offensive content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt our services or servers</li>
              <li>Use our services for spam or unsolicited communications</li>
              <li>Impersonate any person or entity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Intellectual Property</h2>
            <p className="leading-relaxed">
              All content, features, and functionality of our services, including but not limited to text, graphics, 
              logos, and software, are owned by Call24x7.AI and are protected by international copyright, trademark, 
              and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Payment Terms</h2>
            <p className="leading-relaxed mb-3">For paid services:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>All fees are quoted and payable in the currency specified</li>
              <li>Payment is due according to the billing cycle selected</li>
              <li>We reserve the right to change pricing with reasonable notice</li>
              <li>Refunds are subject to our refund policy</li>
              <li>Failed payments may result in service suspension</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Limitation of Liability</h2>
            <p className="leading-relaxed">
              To the maximum extent permitted by law, Call24x7.AI shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, including but not limited to loss of profits, data, or 
              business opportunities, arising from your use of our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Disclaimer of Warranties</h2>
            <p className="leading-relaxed">
              Our services are provided "as is" and "as available" without any warranties of any kind, either express 
              or implied. We do not warrant that our services will be uninterrupted, error-free, or completely secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Indemnification</h2>
            <p className="leading-relaxed">
              You agree to indemnify, defend, and hold harmless Call24x7.AI and its officers, directors, employees, 
              and agents from any claims, damages, losses, or expenses arising from your use of our services or 
              violation of these terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
            <p className="leading-relaxed">
              We may terminate or suspend your account and access to our services immediately, without prior notice, 
              for any breach of these Terms of Use. Upon termination, your right to use our services will cease immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Governing Law</h2>
            <p className="leading-relaxed">
              These terms shall be governed by and construed in accordance with applicable laws, without regard to 
              conflict of law principles. Any disputes arising from these terms shall be resolved in the appropriate courts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Changes to Terms</h2>
            <p className="leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify you of any changes by posting 
              the new terms on this page and updating the "Last updated" date. Your continued use of our services 
              after such changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about these Terms of Use, please contact us at:
            </p>
            <p className="leading-relaxed mt-2">
              Email: hello@call24x7.ai
            </p>
          </section>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsOfUse;
